import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  RedeemCodeResult,
  UserAreaBulkUploadResult,
  UserAreaContactMembershipModel,
  UserAreaContentOrderItem,
  UserAreaCreateUpdateModel,
  UserAreaFileModel,
  UserAreaInviteModel,
  UserAreaLinkModel,
  UserAreaMembershipModel,
  UserAreaModel,
  UserAreaRole,
} from '../models/user-area.model';
import { base64ToFile } from '../utils/file.utils';

@Injectable({
  providedIn: 'root',
})
export class UserAreaService {
  constructor(private http: HttpClient) {}

  getAllAreas(): Observable<UserAreaModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllUserAreas');
    return this.http
      .post<{
        success: boolean;
        data: any[];
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => r.data?.map(a => this.parseArea(a)) ?? []),
        catchError(() => throwError(() => new Error('User areas fetch failed')))
      );
  }

  getMyUserAreas(): Observable<UserAreaModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getMyUserAreas');
    return this.http
      .post<{
        success: boolean;
        data: any[];
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => r.data?.map(a => this.parseArea(a)) ?? []),
        catchError(() => throwError(() => new Error('User areas fetch failed')))
      );
  }

  getArea(areaId: number): Observable<UserAreaModel> {
    const formData = new FormData();
    formData.append('mode', 'getUserArea');
    formData.append('area_id', String(areaId));
    return this.http
      .post<{
        success: boolean;
        data: any;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => this.parseArea(r.data)),
        catchError(() => throwError(() => new Error('User area fetch failed')))
      );
  }

  getMemberships(areaId: number): Observable<UserAreaMembershipModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getUserAreaMemberships');
    formData.append('area_id', String(areaId));
    return this.http
      .post<{
        success: boolean;
        data: any[];
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => r.data?.map(m => this.parseMembership(m)) ?? []),
        catchError(() => throwError(() => new Error('Membership fetch failed')))
      );
  }

  getUserMembershipAreas(
    userId: number
  ): Observable<UserAreaContactMembershipModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getUserMembershipAreas');
    formData.append('user_id', String(userId));
    return this.http
      .post<{
        success: boolean;
        data: any[];
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => r.data?.map(m => this.parseContactMembership(m)) ?? []),
        catchError(() =>
          throwError(() => new Error('Membership areas fetch failed'))
        )
      );
  }

  redeemCode(code: string): Observable<RedeemCodeResult> {
    const formData = new FormData();
    formData.append('mode', 'redeemUserAreaCode');
    formData.append('code', code);
    return this.http
      .post<{
        success: boolean;
        message: string;
        area_id?: number;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(res => {
          if (!res.success) {
            throw new Error(res.message);
          }
          return {
            success: res.success,
            message: res.message,
          } as RedeemCodeResult;
        }),
        catchError(err => throwError(() => err))
      );
  }

  redeemAreaCode(code: string): Observable<number> {
    const formData = new FormData();
    formData.append('mode', 'redeemAreaCode');
    formData.append('code', code);
    return this.http
      .post<{
        success: boolean;
        message: string;
        data: number;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) throw new Error(r.message);
          return r.data;
        }),
        catchError(err =>
          throwError(
            () =>
              new Error(err?.error?.message || err?.message || 'Code ungültig')
          )
        )
      );
  }

  inviteUser(invite: UserAreaInviteModel): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'inviteUserToArea');
    formData.append('area_id', String(invite.area_id));
    formData.append('email', invite.userEmail);
    formData.append('role', invite.role);
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) {
            throw new Error(r.message);
          }
        }),
        catchError(() => throwError(() => new Error('Invite failed')))
      );
  }

  updateUserRole(
    areaId: number,
    userId: number,
    role: UserAreaRole
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateUserAreaRole');
    formData.append('area_id', String(areaId));
    formData.append('user_id', String(userId));
    formData.append('role', role);
    return this.http
      .post<void>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        catchError(() => throwError(() => new Error('Role update failed')))
      );
  }

  removeUserFromArea(areaId: number, userId: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'removeUserFromArea');
    formData.append('area_id', String(areaId));
    formData.append('user_id', String(userId));
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) {
            throw new Error(r.message);
          }
        }),
        catchError(err =>
          throwError(
            () => new Error(err?.error?.message || 'Remove member failed')
          )
        )
      );
  }

  getAreaFiles(areaId: number): Observable<UserAreaFileModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getUserAreaFiles');
    formData.append('area_id', String(areaId));
    return this.http
      .post<{
        success: boolean;
        data: any[];
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => r.data?.map(f => this.parseFile(f)) ?? []),
        catchError(() => throwError(() => new Error('Files fetch failed')))
      );
  }

  uploadAreaFile(areaId: number, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'uploadUserAreaFile');
    formData.append('area_id', String(areaId));
    formData.append('file', file);
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) {
            throw new Error(r.message);
          }
        }),
        catchError(() => throwError(() => new Error('File upload failed')))
      );
  }

  uploadAreaFileBulk(
    areaIds: number[],
    file: File
  ): Observable<UserAreaBulkUploadResult> {
    const formData = new FormData();
    formData.append('mode', 'uploadUserAreaFileBulk');
    formData.append('area_ids', JSON.stringify(areaIds));
    formData.append('file', file);

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: {
          uploaded_area_ids: number[];
          failed_areas: { area_id: number; message: string }[];
          total_requested: number;
          total_uploaded: number;
        };
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => ({
          uploadedAreaIds: r.data?.uploaded_area_ids ?? [],
          failedAreas:
            r.data?.failed_areas?.map(item => ({
              areaId: +item.area_id,
              message: item.message,
            })) ?? [],
          totalRequested: r.data?.total_requested ?? 0,
          totalUploaded: r.data?.total_uploaded ?? 0,
        })),
        catchError(err => throwError(() => err))
      );
  }

  reorderAreaContent(
    areaId: number,
    items: UserAreaContentOrderItem[]
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'reorderUserAreaContent');
    formData.append('area_id', String(areaId));
    formData.append('items', JSON.stringify(items));

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) {
            throw new Error(r.message || 'Order update failed');
          }
        }),
        catchError(err =>
          throwError(
            () => new Error(err?.error?.message || 'Order update failed')
          )
        )
      );
  }

  activateInvite(areaId: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'activateUserAreaInvite');
    formData.append('area_id', String(areaId));
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) {
            throw new Error(r.message);
          }
        }),
        catchError(() =>
          throwError(() => new Error('Invite activation failed'))
        )
      );
  }

  createUserArea(payload: UserAreaCreateUpdateModel, image?: File | null) {
    const formData = new FormData();
    formData.append('mode', 'createUserArea');
    formData.append('name', payload.name);
    formData.append('description', payload.description);
    if (payload.ownerId) {
      formData.append('owner_id', String(payload.ownerId));
    }
    if (payload.contactEmail) {
      formData.append('contact_email', payload.contactEmail);
    }
    if (image) {
      formData.append('image', image);
    }
    return this.http.post<{
      success: boolean;
      message: string;
      area_id?: number;
    }>(environment.authority_short + 'user_area.php', formData);
  }

  createPublicUserArea(
    payload: UserAreaCreateUpdateModel,
    image?: File | null
  ) {
    const formData = new FormData();
    formData.append('mode', 'createPublicUserArea');
    formData.append('name', payload.name);
    formData.append('description', payload.description);
    if (payload.contactEmail) {
      formData.append('contact_email', payload.contactEmail);
    }
    if (image) {
      formData.append('image', image);
    }
    return this.http.post<{
      success: boolean;
      message: string;
      area_id?: number;
    }>(environment.authority_short + 'user_area.php', formData);
  }

  updateUserArea(
    id: number,
    payload: UserAreaCreateUpdateModel,
    image?: File | null
  ) {
    const formData = new FormData();
    formData.append('mode', 'updateUserArea');
    formData.append('id', String(id));
    formData.append('name', payload.name);
    formData.append('description', payload.description);
    if (payload.ownerId) {
      formData.append('owner_id', String(payload.ownerId));
    }
    if (payload.contactEmail !== undefined) {
      formData.append('contact_email', payload.contactEmail ?? '');
    }
    if (image) {
      formData.append('image', image);
    }
    return this.http.post<{
      success: boolean;
      message: string;
    }>(environment.authority_short + 'user_area.php', formData);
  }

  deleteAreaFile(fileId: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'deleteUserAreaFile');
    formData.append('file_id', String(fileId));
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) throw new Error(r.message);
        })
      );
  }

  getAreaLinks(areaId: number): Observable<UserAreaLinkModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getUserAreaLinks');
    formData.append('area_id', String(areaId));
    return this.http
      .post<{
        success: boolean;
        data: any[];
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(
          r =>
            r.data?.map(l => ({
              id: +l.id,
              area_id: +l.area_id,
              title: l.title,
              url: l.url,
              createdAt: l.created_at && moment(l.created_at).toDate(),
              createdBy: +l.created_by,
              position: l.position != null ? +l.position : undefined,
            })) ?? []
        )
      );
  }

  createAreaLink(areaId: number, title: string, url: string): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'createUserAreaLink');
    formData.append('area_id', String(areaId));
    formData.append('title', title);
    formData.append('url', url);
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) throw new Error(r.message);
        })
      );
  }

  deleteAreaLink(linkId: number): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'deleteUserAreaLink');
    formData.append('link_id', String(linkId));
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) throw new Error(r.message);
        })
      );
  }

  renameAreaFile(fileId: number, newName: string): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'renameUserAreaFile');
    formData.append('file_id', String(fileId));
    formData.append('new_name', newName);
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) throw new Error(r.message);
        }),
        catchError(err =>
          throwError(() => new Error(err?.message || 'Rename failed'))
        )
      );
  }

  getAreaFileBlob(fileId: number): Observable<Blob> {
    const url = `${environment.authority_short}user_area.php?mode=getUserAreaFile&file_id=${fileId}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  requestUserAreaContact(areaId: number, message: string): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'requestUserAreaContact');
    formData.append('area_id', String(areaId));
    formData.append('message', message);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'user_area.php', formData)
      .pipe(
        map(r => {
          if (!r.success) throw new Error(r.message);
        }),
        catchError(err =>
          throwError(
            () =>
              new Error(
                err?.error?.message ||
                  'Kontaktanfrage konnte nicht gesendet werden.'
              )
          )
        )
      );
  }

  private parseArea(raw: any): UserAreaModel {
    if (!raw) return null;
    return {
      id: +raw.id,
      name: raw.name,
      description: raw.description,
      createdAt: raw.created_at && moment(raw.created_at).toDate(),
      updatedAt: raw.updated_at && moment(raw.updated_at).toDate(),
      isPublic: Boolean(raw.is_public),
      owner: raw.owner
        ? {
            id: +raw.owner.id,
            firstName: raw.owner.first_name,
            lastName: raw.owner.last_name,
          }
        : undefined,
      memberCount: raw.member_count && +raw.member_count,
      role: raw.role as UserAreaRole,
      code: raw.code,
      image: raw.image ? base64ToFile(raw.image, raw.name) : null,
      hasContact: !!raw.has_contact,
      contactEmail: raw.contact_email ?? null,
    };
  }

  private parseMembership(raw: any): UserAreaMembershipModel {
    return {
      area_id: +raw.area_id,
      user_id: +raw.user_id,
      role: raw.role,
      joinedAt: raw.joined_at && moment(raw.joined_at).toDate(),
      firstName: raw.first_name ?? undefined,
      lastName: raw.last_name ?? undefined,
    };
  }

  private parseContactMembership(raw: any): UserAreaContactMembershipModel {
    return {
      area_id: +raw.area_id,
      user_id: +raw.user_id,
      role: raw.role,
      joinedAt: raw.joined_at && moment(raw.joined_at).toDate(),
      areaName: raw.area_name,
      areaDescription: raw.area_description ?? undefined,
      areaOwnerId: raw.area_owner_id ? +raw.area_owner_id : undefined,
      areaMemberCount:
        raw.area_member_count != null ? +raw.area_member_count : undefined,
    };
  }

  private parseFile(raw: any): UserAreaFileModel {
    return {
      id: +raw.id,
      area_id: +raw.area_id,
      fileName: raw.file_name,
      path: raw.path,
      uploadedAt: raw.uploaded_at && moment(raw.uploaded_at).toDate(),
      uploaderId: +raw.uploader_id,
      position: raw.position != null ? +raw.position : undefined,
    };
  }
}
